// Client-side session resolution. The canonical session leaf, its fetcher,
// and the `Member`/`MyContext` types now live in `lib/queries/identity.ts`
// (per the react-query-options-migration: route loaders bypass hooks, so the
// query definition is co-located with the rest of the identity query layer).
// This module re-exports them and adds the React-only consumption surface
// (hooks) plus the session-derived helpers that are not themselves queries.

import { useQuery } from '@tanstack/react-query';
import { photoUrlFromKey } from '@leanin/shared/media-url';
import type { AuthedSession } from '@leanin/shared/auth/session';
import type { PlatformRole } from '@leanin/shared/views/profile';
import {
  sessionQuery,
  type Member,
  type MyContext,
  redirectIfSignedIn,
  gateOnboardingComplete,
  gateOnboardingIncomplete,
} from './queries/identity';

export {
  sessionQuery,
  redirectIfSignedIn,
  gateOnboardingComplete,
  gateOnboardingIncomplete,
};
export type { Member, MyContext };

export function useSession() {
  return useQuery(sessionQuery);
}

export function useMember(): Member | null {
  const { data } = useSession();
  return data?.member ?? null;
}

export function useAuthedSession(): AuthedSession | null {
  const { data } = useSession();
  return data?.session ?? null;
}

export function memberPhotoUrl(m: Member): string | undefined {
  return photoUrlFromKey(m.photoStorageKey, m.photoStatus) ?? undefined;
}

export function viewerForShell(m: Member): { name: string; avatarSrc?: string } {
  const name = `${m.firstName} ${m.lastName}`.trim();
  const photo = memberPhotoUrl(m);
  return photo !== undefined ? { name, avatarSrc: photo } : { name };
}

// Resource authoring is admin-only — mirrors the server-side
// `authorizeCreate` in `apps/server/src/resources/service/create-resource.ts`.
export function mayCreateResource(platformRole: PlatformRole | null): boolean {
  return platformRole === 'admin';
}
